import { useEffect, useRef, useState } from 'react';
import { ApiError } from './api.ts';

export interface Async<T> { data?: T; error?: string; loading: boolean; reload: () => void }

/**
 * Runs an async load when deps change; with pollMs it repeats while the page is visible.
 * Earlier data stays visible while a reload is in flight.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[], pollMs?: number): Async<T> {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: true });
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    loadRef.current()
      .then((data) => !cancelled && setState({ data, loading: false }))
      .catch((e) => !cancelled && setState((s) => ({ ...s, error: e instanceof ApiError ? e.message : String(e), loading: false })));
    return () => {
      cancelled = true;
    };
  }, [...deps, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pollMs) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') setTick((x) => x + 1);
    }, pollMs);
    return () => clearInterval(t);
  }, [pollMs]);

  return { ...state, reload: () => setTick((x) => x + 1) };
}

const RECENT_KEY = 'mv.recent';

export function recentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
  } catch {
    return [];
  }
}

export function rememberSearch(q: string): void {
  if (!q.trim()) return;
  try {
    const list = [q, ...recentSearches().filter((x) => x !== q)].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // storage unavailable: nothing to remember
  }
}
